import { VisibilityOffRounded } from "@mui/icons-material";
import { Link, Stack, SxProps, Tooltip, Typography, useTheme } from "@mui/material";
import { IssueDrawerContent, MaterialSymbolIcon, MaterialSymbols, useDrawer } from "src/components";
import { IssuePrivacyType, Links } from "src/constants";
import type { ParentIssueDto } from "src/http";

interface IProps {
  parentIssue: ParentIssueDto;
  showIcon?: boolean;
  hideTeamName?: boolean;
  condensed?: boolean;
  openDrawerOnClick?: boolean;
  sx?: SxProps;
}

const ParentIssueLabel = (props: IProps) => {
  const { parentIssue } = props;
  const { showDrawer } = useDrawer();
  const theme = useTheme();

  function handleIssueClicked() {
    showDrawer({
      content: <IssueDrawerContent issueGuid={parentIssue.issueGuid} />,
    });
  }

  return (
    <Stack
      direction="row"
      spacing={0.25}
      alignItems="center"
      sx={{ flexGrow: 1, minWidth: "0px", width: "100%", ...props.sx }}
    >
      {props.showIcon && (
        <MaterialSymbolIcon symbol={MaterialSymbols.QuickPhrases} size={props.condensed ? "small" : "medium"} />
      )}
      {parentIssue.privacy === IssuePrivacyType.Limited && !parentIssue.userCanView ? (
        <Stack direction="row" spacing={0.25} alignItems="center">
          <Typography
            variant="body1"
            noWrap
            sx={{ fontSize: props.condensed ? 12 : "unset", color: theme.palette.error.main }}
          >
            Context loop has limited visibility
          </Typography>
          <VisibilityOffRounded sx={{ color: theme.palette.error.main }} />
        </Stack>
      ) : (
        <Tooltip
          placement="top-start"
          enterDelay={1000}
          enterNextDelay={1000}
          leaveDelay={500}
          title={
            <Typography variant="body1">
              {parentIssue.assignedTeam && (
                <>
                  <Link
                    href={Links.TeamImplementation(parentIssue.assignedTeam.slug)}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ fontWeight: "bold", textTransform: "uppercase" }}
                  >
                    {parentIssue.assignedTeam.name}
                  </Link>
                  {" : "}
                </>
              )}

              <Link
                href={Links.IssueDetail(parentIssue.issueGuid)}
                onClick={(e) => {
                  if (props.openDrawerOnClick) {
                    e.preventDefault();
                    handleIssueClicked();
                  }
                  e.stopPropagation();
                }}
              >
                {parentIssue.description}
              </Link>
            </Typography>
          }
        >
          <Stack
            direction="row"
            spacing={0.25}
            alignItems="center"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <>
              {parentIssue.assignedTeam && !props.hideTeamName && (
                <>
                  <Link noWrap href={Links.Team(parentIssue.assignedTeam.slug)} onClick={(e) => e.stopPropagation()}>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{ fontSize: props.condensed ? 12 : "unset", textTransform: "uppercase" }}
                    >
                      {parentIssue.assignedTeam.name}
                    </Typography>
                  </Link>

                  <Typography
                    variant="body2"
                    sx={{ pb: 0.1, fontSize: props.condensed ? 12 : "unset", mx: 0.25 }}
                    color={theme.palette.grey[300]}
                  >
                    :
                  </Typography>
                </>
              )}

              <Link
                noWrap
                href={Links.IssueDetail(parentIssue.issueGuid)}
                onClick={(e) => {
                  if (props.openDrawerOnClick) {
                    e.preventDefault();
                    handleIssueClicked();
                  }
                  e.stopPropagation();
                }}
              >
                <Typography variant="body1" noWrap sx={{ fontSize: props.condensed ? 12 : "unset" }}>
                  {parentIssue.description}
                </Typography>
              </Link>
            </>
          </Stack>
        </Tooltip>
      )}
    </Stack>
  );
};

export { ParentIssueLabel };
